import { computeInsight, type InsightResult } from './insight-compute'
import {
  insightSegments,
  insightValues,
  matchesInsightFilters,
  type InsightRow,
} from './insight-rows'
import {
  insightDimensionLabel,
  type InsightDimension,
  type InsightFilter,
  type InsightLabelFilter,
  type InsightSpec,
} from './insight-spec'
import type { InsightResultDigest } from './insight-wire'

// ── Checking a chart against the articles ───────────────────────────────────────────────────

export interface InsightCritique {
  /** Problems worth another try: an empty chart, a filter value the coverage does not have. */
  blocking: string[]
  /** Worth knowing, not worth redrawing: a colour or rename for a label that is not on screen. */
  warnings: string[]
}

/** Values of a field across the scope, most frequent first. */
function valuesOf(scope: InsightRow[], field: Exclude<InsightDimension, 'date'>) {
  const counts = new Map<string, number>()
  for (const row of scope) {
    for (const value of insightValues(row, field)) counts.set(value, (counts.get(value) ?? 0) + 1)
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value]) => value)
}

/** The closest real values to one that is not there — shared words or a shared prefix. */
function closest(candidates: string[], wanted: string): string[] {
  const needle = wanted.toLowerCase()
  const words = needle.split(/[^a-z0-9]+/).filter((word) => word.length > 2)
  return candidates
    .filter((value) => {
      const lower = value.toLowerCase()
      return (
        lower.includes(needle) ||
        needle.includes(lower) ||
        lower.slice(0, 4) === needle.slice(0, 4) ||
        words.some((word) => lower.includes(word))
      )
    })
    .slice(0, 3)
}

/**
 * A chart checked against the articles it is drawn from — what Coverage Lab looks at before it
 * shows anything. An empty chart, or a filter on a value the coverage does not have, is blocking:
 * the model gets one more try, told exactly what is wrong and what the real values are. Smaller
 * things (a colour for a label not on screen, a donut with twenty slices) are warnings, shown as a
 * note under the chart.
 */
/** What a chart draws, as one comparable string — its numbers, not how they look. */
function drawnNumbers(result: InsightResult): string {
  return JSON.stringify([
    result.value,
    result.rows.map((row) => [row.label, row.values, row.secondary]),
    result.points.map((point) => [point.label, point.x, point.y, point.size]),
    result.facets.map((facet) => [facet.label, drawnNumbers(facet.result)]),
  ])
}

/** The parts of a spec that decide its numbers — everything but titles and looks — as one string:
 *  two specs with the same key draw the same numbers over the same rows. */
export function insightDataKey(spec: InsightSpec): string {
  const {
    title: _t,
    subtitle: _s,
    style: _st,
    labels: _l,
    order: _o,
    highlight: _h,
    ...data
  } = spec
  return JSON.stringify({ ...data, chart: null, reference: null, sort: null })
}

const quoted = (values: string[]) => values.map((value) => `“${value}”`).join(', ')

/** Every exclusion in a spec (`notIn` / `notContains`, at the top or in a segment), each with the
 *  spec it leaves when taken away. */
function exclusionsOf(spec: InsightSpec): Array<{ filter: InsightFilter; without: InsightSpec }> {
  const excludes = (filter: InsightFilter) =>
    'op' in filter && (filter.op === 'notIn' || filter.op === 'notContains')
  return [
    ...spec.where.flatMap((filter, index) =>
      excludes(filter)
        ? [{ filter, without: { ...spec, where: spec.where.filter((_, i) => i !== index) } }]
        : [],
    ),
    ...spec.segments.flatMap((segment, s) =>
      segment.where.flatMap((filter, index) =>
        excludes(filter)
          ? [
              {
                filter,
                without: {
                  ...spec,
                  segments: spec.segments.map((other, j) =>
                    j === s
                      ? { ...other, where: other.where.filter((_, i) => i !== index) }
                      : other,
                  ),
                },
              },
            ]
          : [],
      ),
    ),
  ]
}

/**
 * Checks a chart against the articles before anyone sees it. `onScreen` is the chart it replaces,
 * when there is one: a new version whose filters changed but whose numbers did not is a change
 * that did nothing — the analyst asked for a difference and would be shown the same chart.
 */
export function critiqueInsight(
  scope: InsightRow[],
  spec: InsightSpec,
  result: InsightResult,
  onScreen?: { spec: InsightSpec; result: InsightResult } | null,
): InsightCritique {
  const blocking = filterProblems(scope, spec)
  if (blocking.length === 0 && result.matched === 0) {
    blocking.push(
      'The chart matches no articles in scope — a filter or the date range is too narrow.',
    )
  }
  if (blocking.length === 0) blocking.push(...idleExclusions(scope, spec, result))
  if (
    blocking.length === 0 &&
    onScreen &&
    insightDataKey(onScreen.spec) !== insightDataKey(spec) &&
    drawnNumbers(onScreen.result) === drawnNumbers(result)
  ) {
    blocking.push(
      'This version draws exactly the same numbers as the chart on screen, although its filters or groups changed — the change has no effect on these articles. Make the change take effect, or say plainly why it cannot.',
    )
  }
  const warnings = [
    ...result.notes,
    ...offScreenWarnings(spec, result),
    ...shapeWarnings(spec, result),
  ]
  return { blocking, warnings: [...new Set(warnings)] }
}

/** A field's values across the scope, read once per field however many filters name it. */
function knownValues(scope: InsightRow[]) {
  const cache = new Map<string, string[]>()
  return (field: Exclude<InsightDimension, 'date'>) => {
    let values = cache.get(field)
    if (!values) {
      values = valuesOf(scope, field)
      cache.set(field, values)
    }
    return values
  }
}

/** Every filter value — at the top or in a segment — that the coverage does not have. */
function filterProblems(scope: InsightRow[], spec: InsightSpec): string[] {
  const known = knownValues(scope)
  const blocking: string[] = []
  const filters = [...spec.where, ...insightSegments(spec).flatMap((segment) => segment.where)]
  for (const filter of filters) {
    if (filter.field === 'reach') continue
    if (filter.field === 'text') blocking.push(...missingWords(scope, filter))
    else blocking.push(...missingLabels(filter, known(filter.field)))
  }
  return blocking
}

function missingWords(
  scope: InsightRow[],
  filter: Extract<InsightFilter, { field: 'text' }>,
): string[] {
  return filter.values
    .filter(
      (word) =>
        !scope.some((row) =>
          matchesInsightFilters(row, [{ ...filter, op: 'contains', values: [word] }]),
        ),
    )
    .map((word) => `No article in scope mentions “${word}”.`)
}

function missingLabels(filter: InsightLabelFilter, known: string[]): string[] {
  const have = new Set(known.map((value) => value.toLowerCase()))
  return filter.values
    .filter((value) => !have.has(value.toLowerCase()))
    .map(
      (value) =>
        `“${value}” is not a ${insightDimensionLabel(filter.field).toLowerCase()} value in this coverage${offerFor(known, value)}.`,
    )
}

/** The closest real spellings — or, when nothing is close, what IS there, most common first, so
 *  the next try has something real to reach for. */
function offerFor(known: string[], value: string): string {
  const near = closest(known, value)
  if (near.length) return ` — closest: ${near.map((v) => `“${v}”`).join(', ')}`
  if (known.length) {
    return ` — the most common here are ${known
      .slice(0, 5)
      .map((v) => `“${v}”`)
      .join(', ')}`
  }
  return ''
}

/** An exclusion is only real if the chart moves without it. One that removes articles none of
 *  the chart's groups count — the story is tagged differently from the segment it was meant to
 *  leave — reads as applied while drawing exactly what it would without it. */
function idleExclusions(scope: InsightRow[], spec: InsightSpec, result: InsightResult): string[] {
  const drawn = drawnNumbers(result)
  return exclusionsOf(spec)
    .filter(({ without }) => drawnNumbers(computeInsight(scope, without)) === drawn)
    .map(({ filter }) => {
      const what =
        filter.field === 'text'
          ? `articles mentioning ${quoted('values' in filter ? filter.values : [])}`
          : `${insightDimensionLabel(filter.field as InsightDimension).toLowerCase()} ${quoted('values' in filter ? filter.values : [])}`
      return `Excluding ${what} changes nothing on this chart: none of the articles it removes are counted in any of its groups. Check with sample_rows which labels those articles carry, and exclude them where they are counted — or, if they are not in the chart at all, say so.`
    })
}

/** A colour, highlight or new name set for a label the chart does not draw. */
function offScreenWarnings(spec: InsightSpec, result: InsightResult): string[] {
  if (result.chart === 'kpi') return []
  const drawn = new Set(
    [
      ...result.rows.map((row) => row.label),
      ...result.seriesKeys,
      ...result.points.map((point) => point.label),
      ...result.facets.flatMap((facet) => facet.result.rows.map((row) => row.label)),
    ].map((label) => label.toLowerCase()),
  )
  const offScreen = (labels: string[], what: string) => {
    const missing = labels.filter((label) => !drawn.has(label.toLowerCase()))
    return missing.length > 0
      ? [
          `${what} for ${missing.map((l) => `“${l}”`).join(', ')}, which ${missing.length === 1 ? 'is' : 'are'} not on this chart.`,
        ]
      : []
  }
  return [
    ...offScreen(Object.keys(spec.style.colors), 'A colour is set'),
    ...offScreen(spec.highlight, 'A highlight is set'),
    ...offScreen(Object.keys(spec.labels), 'A new name is set'),
  ]
}

/** A chart whose shape reads badly: too many slices, one time bucket, zeros on a log scale. */
function shapeWarnings(spec: InsightSpec, result: InsightResult): string[] {
  const warnings: string[] = []
  if ((spec.chart === 'donut' || spec.chart === 'treemap') && result.rows.length > 10) {
    warnings.push(
      `${result.rows.length} slices is hard to read — a ranked bar chart, or a lower limit, reads better.`,
    )
  }
  if (spec.x === 'date' && result.rows.length === 1) {
    warnings.push('Only one time bucket — a smaller bucket (day or week) shows the shape.')
  }
  if (
    spec.style.yScale === 'log' &&
    result.rows.some((row) => Object.values(row.values).some((v) => v !== null && v <= 0))
  ) {
    warnings.push('A log scale cannot draw zero, so empty buckets are left out.')
  }
  return warnings
}

/** The result as the model sees it: labels and numbers, capped. */
export function digestInsight(result: InsightResult): InsightResultDigest {
  if (result.chart === 'kpi') {
    return { rows: [{ label: result.measureLabel, values: { value: result.value } }] }
  }
  if (result.points.length > 0 && result.pointLabels) {
    return {
      rows: result.points.slice(0, 60).map((point) => ({
        label: point.label.slice(0, 200),
        values: {
          [result.pointLabels!.x]: point.x,
          [result.pointLabels!.y]: point.y,
          ...(result.pointLabels!.size && { [result.pointLabels!.size]: point.size }),
        },
      })),
    }
  }
  const rows = (prefix: string, source: InsightResult) =>
    source.rows.map((row) => ({
      label: `${prefix}${row.label}`.slice(0, 200),
      values: {
        ...Object.fromEntries(
          Object.entries(row.values)
            .slice(0, 12)
            .map(([key, value]) => [key.slice(0, 200), value]),
        ),
        ...(source.secondaryLabel && { [source.secondaryLabel]: row.secondary }),
      },
    }))
  const all =
    result.facets.length > 0
      ? result.facets.flatMap((facet) => rows(`${facet.label} · `, facet.result))
      : rows('', result)
  return { rows: all.slice(0, 60) }
}
